import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { maskPhone } from '@givebutter/util';
import { useSaveContact } from './hooks/useSaveContact';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import { useDefaultContactFormValues } from 'assets/js/pages/contacts/hooks/useDefaultContactFormValues';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { ContactType } from 'constants/contacts';
import { getContactName } from 'customHooks/contactNameDisplay';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';

const ConvertContactFormDrawer = () => {
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const contactListMatch = useRouteMatch('/accounts/:account_id/contacts/convert/:contact_id');

    const [isAddingContactFile, setIsAddingContactFile] = useState(false);

    const defaultValues = useDefaultContactFormValues(
        contact.type === ContactType.Company
            ? {
                  companyName: '',
                  firstName: contact.company_name,
                  type: ContactType.Individual,
                  ui: {
                      showPrefix: false,
                  },
              }
            : {
                  firstName: '',
                  lastName: '',
                  companyName: getContactName(contact),
                  companyEmails: contact.emails.map(email => ({
                      isPrimary: email.is_primary,
                      value: email.value,
                      type: 'work',
                  })),
                  companyPhones: contact.phones.map(phone => ({
                      isPrimary: phone.is_primary,
                      value: maskPhone(phone.value),
                      type: 'work',
                  })),
                  type: ContactType.Company,
              },
    );

    const methods = useGivebutterForm<ContactFormValues>({
        defaultValues,
    });

    const { handleSubmit, watch } = methods;

    const contactType = watch('type');

    const { mutate: submit, isPending } = useSaveContact(methods, {
        onSuccess: () => {
            const type = contactType === ContactType.Company ? 'companies' : 'contacts';
            const url = contactListMatch
                ? `/accounts/${accountId}/${type}`
                : `/accounts/${accountId}/${type}/${contact.id}`;
            history.replace(url);
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>
                        Convert to {contact.type === ContactType.Company ? 'individual' : 'company'}
                    </Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <ContactForm.Container>
                        {contactType === ContactType.Company ? (
                            <>
                                <CompanyForm.Name />
                                <ContactForm.Addresses />
                                <ContactForm.Emails name="companyEmails" />
                                <ContactForm.Phones name="companyPhones" />
                                <ContactForm.Separator />
                                <CompanyForm.PointOfContact />
                                <ContactForm.Separator />
                                <ContactForm.Socials />
                            </>
                        ) : (
                            <>
                                <ContactForm.FirstName hideAffix />
                                <ContactForm.MiddleName />
                                <ContactForm.LastName />
                                <ContactForm.Dob />
                                <ContactForm.Gender />
                                <ContactForm.Employer />
                                <CompanyForm.CompanySelectForm />
                                <ContactForm.Title />
                                <ContactForm.Separator />
                                <ContactForm.Emails />
                                <ContactForm.Separator />
                                <ContactForm.Phones />
                                <ContactForm.Separator />
                                <ContactForm.Addresses />
                            </>
                        )}
                        <ContactForm.Separator />
                        <ContactForm.Notes />
                        <ContactForm.Tags />
                        <ContactForm.Separator />
                        <ContactForm.CustomFields
                            onAddContactFile={setIsAddingContactFile}
                            type={contactType}
                        />
                        <ContactForm.Separator />
                        <ContactForm.Engage />
                    </ContactForm.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant={'none'}>Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton isSubmitting={isAddingContactFile || isPending}>
                                Save
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const ConvertContactFormDrawerTrigger = Drawer.Trigger;

const Root = ConvertContactFormDrawer;
const Trigger = ConvertContactFormDrawerTrigger;

export {
    ConvertContactFormDrawer,
    ConvertContactFormDrawerTrigger,
    //
    Root,
    Trigger,
};
